/* Accessibility enhancements – loaded after the theme script. Each block notes its checklist item. */

/* Screen reader text comes translated from snippets/theme-globals.liquid (locales: general.accessibility.*);
   the English here is only a fallback. a11yText('itemAdded', { title }) fills the [title] placeholder. */
const a11yText = (key, values = {}) => {
  const fallback = {
    cartCountOne: 'Cart, [count] item',
    cartCountOther: 'Cart, [count] items',
    itemAdded: '[title] added to bag.',
    itemQuantity: '[title]: quantity updated to [quantity].',
    itemRemoved: '[title] removed.',
    summaryOne: '[count] item, subtotal [subtotal].',
    summaryOther: '[count] items, subtotal [subtotal].',
    cartEmpty: 'Your bag is empty.',
  };
  const text = (window.a11yStrings && window.a11yStrings[key]) || fallback[key];
  return text.replace(/\[(\w+)\]/g, (match, name) => (name in values ? values[name] : match));
};

/* #2 – Keep keyboard focus in the cart drawer after it re-renders (WCAG 2.4.3).
   The theme replaces the cart items' HTML after every quantity change / Remove, which destroys
   the focused button and drops focus to <body>. Remember the cart control that last had focus, then
   put focus back on the same control, the next item's control, or the cart heading if the cart is now empty. */
(() => {
  const cart = document.querySelector('.quick-cart');
  if (!cart) return;

  const CONTROLS = ['[data-add-quantity]', '[data-subtract-quantity]', '[data-remove-item]', '[data-quantity-input]'];
  let last = null; // the cart control that most recently had focus (or was clicked)
  let expires = 0; // only restore within 10s of a cart update starting

  const describe = (target) => {
    const control = target.closest(CONTROLS.join(','));
    const item = control && control.closest('.quick-cart__item[data-key]');
    if (!item) return null;
    return {
      key: item.dataset.key,
      selector: CONTROLS.find((selector) => control.matches(selector)),
      index: [...cart.querySelectorAll('.quick-cart__item[data-key]')].indexOf(item),
    };
  };
  const remember = (event) => {
    last = describe(event.target) || last;
    if (event.type !== 'focusin') expires = Date.now() + 10000;
  };
  cart.addEventListener('focusin', remember, { capture: true });
  cart.addEventListener('click', remember, { capture: true }); // Safari doesn't focus buttons on click
  cart.addEventListener('change', remember, { capture: true });

  const restore = () => {
    if (!last || Date.now() > expires) return;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return; // focus wasn't lost yet

    const items = [...cart.querySelectorAll('.quick-cart__item[data-key]')];
    const item = items.find((el) => el.dataset.key === last.key) // same item (quantity change)
      || items[last.index] // next item slid into the removed item's place
      || items[last.index - 1]; // removed the last item in the list
    let target = item && item.querySelector(last.selector);

    if (!target) {
      target = cart.querySelector('.quick-cart__heading'); // cart is now empty
      if (!target) return;
      target.setAttribute('tabindex', '-1');
    }
    expires = 0;
    target.focus();
  };
  new MutationObserver(restore).observe(cart, { childList: true, subtree: true });
})();

/* #3 – Announce cart changes to screen readers (WCAG 4.1.3).
   Listens for the theme's own cart events (enabled via useCustomEvents in theme-globals.liquid):
   "cart:updated" carries the new cart, which is compared with the previous one to describe what changed,
   and "quick-cart:open" tells us when the drawer is visible so the message can be heard. */
(() => {
  const wrapper = document.querySelector('.quick-cart__wrapper');
  if (!wrapper) return;

  // Lives in the wrapper (never re-rendered) so it stays inside the drawer once it becomes a dialog (#5)
  const region = document.createElement('div');
  region.className = 'visually-hidden';
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  wrapper.appendChild(region);

  const isOpen = () => wrapper.classList.contains('active');
  const money = (cents, currency) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);

  // Cart state as { key: { title, quantity } }, from the server-rendered drawer on load, then from each event
  const fromDrawer = () => {
    const items = new Map();
    wrapper.querySelectorAll('.quick-cart__item[data-key]').forEach((item) => {
      const title = [item.querySelector('.quick-cart__item-title'), item.querySelector('.quick-cart__item-variant')]
        .filter(Boolean).map((el) => el.textContent.trim()).join(', ');
      items.set(item.dataset.key, { title, quantity: Number(item.querySelector('[data-quantity-input]')?.value) || 0 });
    });
    return items;
  };
  const fromCart = (cart) => new Map(cart.items.map((item) => [item.key, {
    title: [item.product_title, item.variant_title].filter(Boolean).join(', '),
    quantity: item.quantity,
  }]));

  const describeChanges = (before, after, cart, changedInDrawer) => {
    const changes = [];
    after.forEach((item, key) => {
      const previous = before.get(key);
      if (!previous || (item.quantity > previous.quantity && !changedInDrawer)) changes.push(a11yText('itemAdded', { title: item.title }));
      else if (item.quantity !== previous.quantity) changes.push(a11yText('itemQuantity', { title: item.title, quantity: item.quantity }));
    });
    before.forEach((item, key) => {
      if (!after.has(key)) changes.push(a11yText('itemRemoved', { title: item.title }));
    });
    if (!changes.length) return '';
    const count = cart.item_count;
    const summary = count
      ? a11yText(count === 1 ? 'summaryOne' : 'summaryOther', { count, subtotal: money(cart.total_price, cart.currency) })
      : a11yText('cartEmpty');
    return `${changes.join(' ')} ${summary}`;
  };

  const announce = (message) => {
    // A fresh node each time, so screen readers announce it even if the text repeats
    const line = document.createElement('p');
    line.textContent = message;
    region.replaceChildren(line);
  };

  let previous = fromDrawer();
  let queued = '';

  document.addEventListener('cart:updated', ({ detail }) => {
    if (!detail || !detail.cart) return;
    const current = fromCart(detail.cart);
    const message = describeChanges(previous, current, detail.cart, isOpen());
    previous = current;
    if (!message) return;
    // Text in the hidden drawer isn't read; adds from a product open the drawer just after this event
    if (isOpen()) announce(message);
    else queued = message;
  });
  document.addEventListener('quick-cart:open', () => {
    if (queued) announce(queued);
    queued = '';
  });
  document.addEventListener('quick-cart:close', () => { queued = ''; });
})();

/* #5 – Name the quick view dialog after its product once loaded (theme event "quickview:loaded").
   Until then (and after close) it falls back to aria-label "Quick View". */
(() => {
  const modal = document.querySelector('[data-quick-view-modal]');
  if (!modal) return;
  document.addEventListener('quickview:loaded', () => {
    const title = modal.querySelector('.product__title');
    if (!title) return;
    title.id = title.id || 'QuickViewTitle';
    modal.setAttribute('aria-labelledby', title.id);
  });
})();

/* #6 – Esc closes the cart drawer (WCAG 2.1.1, 2.4.3).
   The theme's focus trap handles Esc by releasing focus (back to whatever opened the drawer)
   but never closes the drawer itself, leaving it open with focus behind it. Close it the same
   way the Close button does. */
(() => {
  const wrapper = document.querySelector('.quick-cart__wrapper');
  if (!wrapper) return;
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !wrapper.classList.contains('active')) return;
    wrapper.querySelector('.quick-cart__close-icon')?.click();
  });

  // The theme's focus trap returns focus to whatever had it when the drawer opened. If nothing did
  // (opened by mouse in Safari, or that element is gone), focus is left in the hidden drawer or on <body>.
  // Send it to the cart icon instead.
  document.addEventListener('quick-cart:close', () => {
    setTimeout(() => {
      const active = document.activeElement;
      if (active && active !== document.body && !wrapper.contains(active)) return; // focus already went somewhere sensible
      [...document.querySelectorAll('[data-js-cart-icon]')].find((icon) => icon.getClientRects().length)?.focus();
    }, 600); // the theme releases its trap 500ms after closing
  });
})();

/* #38 / #9 – Cart icon names the item count ("Cart, 3 items") and stays current (WCAG 1.3.1, 4.1.2).
   The server renders the initial label in sections/header.liquid; the header prints the icon in more than one layout. */
(() => {
  const icons = document.querySelectorAll('[data-js-cart-icon]');
  if (!icons.length) return;
  document.addEventListener('cart:updated', ({ detail }) => {
    if (!detail || !detail.cart) return;
    const count = detail.cart.item_count;
    icons.forEach((icon) => icon.setAttribute('aria-label', a11yText(count === 1 ? 'cartCountOne' : 'cartCountOther', { count })));
  });
})();

/* #14 – Header nav: bring a focused top-level link fully into view (the theme doesn't scroll on focus).
   The scroll buttons are hidden while tabbing (custom.css), so the nav can scroll all the way to either end.
   Scrolls only the nav (not scrollIntoView, which also scrolls the page sideways when it overflows),
   centering the link; the browser clamps at either end. */
(() => {
  const viewport = document.querySelector('.header__links-primary-scroll-container [data-scroll-container-viewport]');
  if (!viewport) return;
  viewport.addEventListener('focusin', (event) => {
    const link = event.target.closest('.header__links-list > li > a');
    if (!link) return;
    const shown = viewport.getBoundingClientRect();
    const box = link.getBoundingClientRect();
    viewport.scrollLeft += box.left + box.width / 2 - (shown.left + shown.width / 2);
  });
})();

/* #13 – Desktop menus: Esc from inside a dropdown returns focus to its trigger ("Shop", "About").
   The theme closes the dropdown on Esc but leaves focus on the now-hidden link (it drops to <body>). */
(() => {
  const nav = document.querySelector('.header__links-primary');
  if (!nav) return;
  nav.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const item = event.target.closest('.header__links-list > li');
    const trigger = item && item.querySelector(':scope > [data-dropdown-trigger]');
    if (!trigger || event.target === trigger) return;
    trigger.focus(); // runs before the theme's own Esc handler, which then closes the dropdown
  }, { capture: true });
})();

/* #13 – Desktop menus: keep keyboard-opened dropdowns open when the mouse isn't moving.
   When a dropdown or the page overlay appears or disappears under a resting pointer, Chrome fires
   mouseover/mouseout even though the mouse didn't move, and the theme's hover handlers close every menu.
   While the keyboard was used more recently than the mouse actually moved, those header and overlay mouse
   events are ignored; real mouse movement hands control back to hover. (WCAG 2.1.1, 2.4.7) */
(() => {
  let x = null;
  let y = null;
  let keyboardLast = false;
  document.addEventListener('keydown', () => { keyboardLast = true; }, true);
  document.addEventListener('mousemove', (event) => {
    if (event.clientX === x && event.clientY === y) return;
    x = event.clientX;
    y = event.clientY;
    keyboardLast = false;
  }, { capture: true, passive: true });
  const ignore = (event) => {
    if (keyboardLast && event.target.closest?.('[data-section-type="header"], .header-overlay')) event.stopPropagation();
  };
  document.addEventListener('mouseover', ignore, true);
  document.addEventListener('mouseout', ignore, true);
})();

/* #13 – Desktop menus: dim the page when a dropdown is opened from the keyboard, as hover does.
   On keyboard focus the theme opens the menu, closes all menus, then opens it again in one go; its overlay
   hide step runs a moment later and wins, so the dropdown opens without the dimmed background.
   Shortly after focus moves within the nav, show the overlay if a dropdown is actually open. */
(() => {
  const nav = document.querySelector('.header__links-primary');
  const overlay = document.querySelector('.header-overlay');
  const inner = overlay && overlay.querySelector('.header-overlay__inner');
  if (!nav || !inner) return;
  let timer;
  nav.addEventListener('focusin', () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (!nav.querySelector('.header__links-list > li > [data-submenu].active') && !document.querySelector('.meganav.active')) return;
      overlay.classList.add('is-active');
      inner.classList.add('is-visible');
    }, 50);
  });
})();

/* #13 – Mobile menu submenus: move focus to a submenu's Back button when it opens and back to its parent
   link when it closes, and take links on the off-screen menu levels out of the Tab order and the
   accessibility tree (tabindex="-1" + aria-hidden, which the theme's focus trap respects; inert is not). */
(() => {
  const drawer = document.querySelector('[data-drawer-menu]');
  const primary = drawer && drawer.querySelector('[data-primary-container]');
  if (!primary) return;

  const HIDDEN = 'data-a11y-hidden';
  const FOCUSABLE = 'a[href], button, [tabindex]';
  const hide = (el) => {
    if (!el) return;
    el.setAttribute(HIDDEN, el.getAttribute('aria-hidden') ?? '');
    el.setAttribute('aria-hidden', 'true');
    [el, ...el.querySelectorAll(FOCUSABLE)].filter((f) => f.matches(FOCUSABLE)).forEach((f) => {
      if (!f.hasAttribute('data-a11y-tabindex')) f.setAttribute('data-a11y-tabindex', f.getAttribute('tabindex') ?? '');
      f.setAttribute('tabindex', '-1');
    });
  };
  const restoreAll = () => {
    primary.querySelectorAll(`[${HIDDEN}]`).forEach((el) => {
      const was = el.getAttribute(HIDDEN);
      if (was) el.setAttribute('aria-hidden', was); else el.removeAttribute('aria-hidden');
      el.removeAttribute(HIDDEN);
    });
    primary.querySelectorAll('[data-a11y-tabindex]').forEach((f) => {
      const was = f.getAttribute('data-a11y-tabindex');
      if (was) f.setAttribute('tabindex', was); else f.removeAttribute('tabindex');
      f.removeAttribute('data-a11y-tabindex');
    });
  };
  const deepest = (panels) => panels.find((p) => !panels.some((q) => q !== p && p.contains(q))) || null;
  let previous = [];

  const update = () => {
    const panels = [...primary.querySelectorAll('.drawer-menu__list--sub.visible')];
    const current = deepest(panels);

    // Only the current panel stays reachable: hide sibling items and the parent link at every level above it
    restoreAll();
    for (let panel = current; panel && panel !== primary; panel = panel.parentElement.parentElement) {
      const item = panel.parentElement;
      [...item.parentElement.children].forEach((li) => { if (li !== item) hide(li); });
      hide(panel.previousElementSibling);
    }

    const closed = deepest(previous.filter((p) => !panels.includes(p)));
    previous = panels;
    if (!drawer.classList.contains('active')) return; // drawer is closing; its focus trap returns focus
    if (current && !closed) current.querySelector('[data-item="back"]')?.focus({ preventScroll: true });
    else if (closed) closed.previousElementSibling?.focus({ preventScroll: true });
  };
  new MutationObserver(update).observe(primary, { attributes: true, attributeFilter: ['data-depth'] });
})();

/* #18 – Sort options behave like a standard keyboard menu of choices:
   - Opening Sort from the keyboard moves focus to the selected option (the theme left it on the button,
     so the arrow keys scrolled the page). Waits for the dropdown's fade-in; hidden elements can't take focus.
   - Arrow keys move between options without applying them (the theme re-sorted on every arrow press);
     Space or Enter applies the focused option. Radio groups are one Tab stop by design. */
(() => {
  const button = document.querySelector('.filter-bar__button--sort');
  const panel = document.getElementById('filter-bar-sort');
  if (button && panel) {
    let pending = false;
    const focusSelected = () => {
      pending = false;
      (panel.querySelector('input[type="radio"]:checked') || panel.querySelector('input[type="radio"]'))?.focus();
    };
    new MutationObserver(() => {
      if (button.getAttribute('aria-expanded') !== 'true' || !document.body.classList.contains('user-is-tabbing')) {
        pending = false;
        return;
      }
      if (getComputedStyle(panel).visibility === 'visible') focusSelected(); // no fade (e.g. reduced motion)
      else pending = true;
    }).observe(button, { attributes: true, attributeFilter: ['aria-expanded'] });
    panel.addEventListener('transitionend', (event) => {
      if (pending && event.propertyName === 'visibility') focusSelected();
    });
  }

  document.querySelectorAll('#filter-bar-sort, .filter-drawer__panel--sort [role="radiogroup"]').forEach((group) => {
    group.addEventListener('keydown', (event) => {
      const radio = event.target.closest('input[type="radio"]');
      if (!radio) return;
      const radios = [...group.querySelectorAll('input[type="radio"]')];
      const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
      if (step) {
        event.preventDefault();
        radios[(radios.indexOf(radio) + step + radios.length) % radios.length].focus();
      } else if (event.key === 'Enter' && !radio.checked) {
        event.preventDefault();
        radio.click();
      }
    });
  });
})();

/* #25 – "Add order notes" toggle (cart drawer and /cart): mirror the note's open state on the button.
   The theme toggles aria-hidden on the note container; it also sets aria-expanded there, which isn't valid
   on a div. Listens on document so it survives the drawer re-rendering; the theme's handler runs first. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-order-note-trigger]');
  const input = button && button.parentElement.querySelector('[cart-note-input]');
  if (!input) return;
  button.setAttribute('aria-expanded', String(input.getAttribute('aria-hidden') === 'false'));
  input.removeAttribute('aria-expanded');
});

/* #26 – Keep keyboard-focused items clear of sticky/fixed bars (WCAG 2.4.11).
   The browser scrolls a newly focused element into view respecting scroll-padding, but the theme's
   sticky header and sticky Filter/Sort bars change height as the page scrolls, so a fixed value can't fit.
   Track how much of the top and bottom of the viewport they cover and expose it as CSS variables. */
(() => {
  const TOP = ['.shopify-section-group-header-group', '.filter-bar--sticky', '.mobile-filters'];
  const BOTTOM = ['.swym-heart'];
  const GAP = 8;
  const root = document.documentElement;
  const covers = (el) => {
    const s = getComputedStyle(el);
    return (s.position === 'sticky' || s.position === 'fixed') && s.visibility !== 'hidden' && s.display !== 'none' && el.offsetHeight > 0;
  };
  let queued = false;
  const update = () => {
    queued = false;
    let top = 0;
    let bottom = 0;
    TOP.forEach((sel) => document.querySelectorAll(sel).forEach((el) => {
      if (covers(el)) top = Math.max(top, el.getBoundingClientRect().bottom);
    }));
    BOTTOM.forEach((sel) => document.querySelectorAll(sel).forEach((el) => {
      if (covers(el)) bottom = Math.max(bottom, window.innerHeight - el.getBoundingClientRect().top);
    }));
    root.style.setProperty('--a11y-scroll-padding-top', `${Math.max(0, Math.round(top)) + GAP}px`);
    root.style.setProperty('--a11y-scroll-padding-bottom', `${Math.max(0, Math.round(bottom)) + GAP}px`);
  };
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  update();
})();

/* #19 – The skip link moves focus into <main>, not just the scroll position. <main> is made focusable only for
   that moment: a permanent tabindex="-1" made every click inside the page focus <main>, so the next Tab
   restarted from the top of the content instead of from where the user clicked. */
(() => {
  const skip = document.querySelector('.skip-to-content');
  const main = document.getElementById('main');
  if (!skip || !main) return;
  skip.addEventListener('click', () => {
    main.setAttribute('tabindex', '-1');
    main.focus();
    main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
  });
})();

/* #39 – Keep "Skip to content" the first element: apps (e.g. Swym) insert their own elements at the top of
   <body> after load. Capped so it can't fight another script that also insists on being first. */
(() => {
  const skip = document.querySelector('.skip-to-content');
  if (!skip) return;
  const MAX_MOVES = 5;
  let moves = 0;
  const observer = new MutationObserver(() => keepFirst());
  const keepFirst = () => {
    if (document.body.firstElementChild === skip) return;
    if (moves >= MAX_MOVES) {
      observer.disconnect();
      console.warn(`[a11y] Another script keeps inserting before "Skip to content"; stopped re-ordering after ${MAX_MOVES} moves.`);
      return;
    }
    moves += 1;
    document.body.prepend(skip);
  };
  observer.observe(document.body, { childList: true });
  keepFirst();
})();
